import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router, Link } from '@inertiajs/react'
import { BookOpen } from 'lucide-react'
import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
})

type FormValues = z.infer<typeof schema>

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  errors: Record<string, string[]>
}

export default function New({ user, organization, errors }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors: fieldErrors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  function onSubmit(data: FormValues) {
    router.post('/dashboard/applications', { application: data })
  }

  function serverError(field: string): string | undefined {
    return errors[field]?.[0]
  }

  return (
    <AppShell user={user} organization={organization}>
      <div className="max-w-lg space-y-8">
        <PageHeader
          eyebrow="Applications · new"
          title="New Application"
          description="Create a new application to get API credentials."
        />

        <aside className="surface-card flex items-start gap-3 p-4">
          <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-foreground/70" strokeWidth={1.7} />
          <p className="text-sm text-muted-foreground">
            Once your application is created, you'll receive API credentials.
            Check our{' '}
            <Link href="/docs" className="font-medium text-primary hover:underline">
              API Documentation
            </Link>{' '}
            for integration guides and code examples.
          </p>
        </aside>

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Application details
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Name *
              </Label>
              <Input id="name" {...register('name')} placeholder="My App" className="rounded-md" />
              {(fieldErrors.name?.message || serverError('name')) && (
                <p className="text-xs text-destructive">
                  {fieldErrors.name?.message ?? serverError('name')}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Description
              </Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Optional description"
                rows={3}
                className="rounded-md"
              />
              {serverError('description') && (
                <p className="text-xs text-destructive">{serverError('description')}</p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating…' : 'Create application'}
              </Button>
              <Button variant="outline" asChild>
                <Link href="/dashboard/applications">Cancel</Link>
              </Button>
            </div>
          </form>
        </section>
      </div>
    </AppShell>
  )
}
